export const CHANNEL_TYPES = {
    EMAIL: 'email',
};

export const PROVIDER_TYPES = {
    AWS: 'aws',
    HUBSPOT: 'hubspot',
    MARKETO: 'marketo',
    ELOQUA: 'eloqua',
};
const { HUBSPOT, MARKETO, ELOQUA } = PROVIDER_TYPES;

export const INTEGRATION_SETTINGS = {
    [HUBSPOT]: {
        title: 'Select an email',
        subtitle:
            'Select the HubSpot email to add as a step in this journey.\nEach email can only be used in one journey at a time.',
        placeholder: 'Search email by name, ID, or subject line',
        fields: [
            { key: 'id', label: 'Email ID' },
            { key: 'subject', label: 'Subject line' },
        ],
        tooltip: 'This HubSpot email is currently in use in another journey.',
        infoButtonText: 'Steps to set up your email in HubSpot',
        setupLink:
            'https://support.pendo.io/hc/en-us/articles/28064482675611-Add-messages-to-your-journey#h_01JDSBYERT49RZQ1RZC9NWBC1C',
    },
    [MARKETO]: {
        title: 'Select a smart campaign',
        subtitle:
            'Select the Marketo smart campaign to add as a step in this journey.\nEach campaign can only be used in one journey at a time.',
        placeholder: 'Search campaign by name, ID, or program',
        fields: [
            { key: 'id', label: 'Campaign ID' },
            { key: 'programName', label: 'Program' },
        ],
        tooltip: 'This Marketo campaign is currently in use in another journey.',
        infoButtonText: 'Steps to set up your campaign in Marketo',
        setupLink:
            'https://support.pendo.io/hc/en-us/articles/28064482675611-Add-messages-to-your-journey#01JQPDKF9Z64H5FTA50SF2G06R',
    },
    [ELOQUA]: {
        title: 'Select an email',
        subtitle:
            'Select an Oracle Eloqua email to add as a step in this journey. The list shows up to 500 active emails created in the last 90 days. Each email can only be used in one journey at a time.',
        placeholder: 'Search email by name, ID, or subject line',
        fields: [
            { key: 'id', label: 'Email ID' },
            { key: 'subject', label: 'Subject line' },
        ],
        tooltip: 'This Eloqua email is currently in use in another journey.',
        infoButtonText: 'Steps to set up your email in Oracle Eloqua',
        setupLink:
            'https://support.pendo.io/hc/en-us/articles/28064482675611-Add-messages-to-your-journey',
    },
};

export const INTEGRATION_DATA_STATUSES = {
    LOADING: 'loading',
    EMPTY: 'empty',
    REJECTED: 'rejected',
    NO_RESULTS: 'noResults',
    RESOLVED: 'resolved',
};

const { EMPTY, REJECTED, NO_RESULTS } = INTEGRATION_DATA_STATUSES;

export const INTEGRATION_DATA_STATUS_MESSAGES = {
    [HUBSPOT]: {
        [EMPTY]: {
            title: 'No emails were found in HubSpot',
            description:
                'We couldn’t find any emails. This might mean there are no emails in HubSpot or they aren’t set up to appear in Pendo.\nMake sure your email is public and configured correctly.',
        },
        [REJECTED]: {
            title: 'Unable to retrieve email list from HubSpot',
            description:
                'Please double-check your integration settings and try again. If the issue continues, please contact support.',
        },
        [NO_RESULTS]: {
            title: 'No emails found',
            description:
                'No emails match your search. If you don’t see an expected email, ensure it’s set up correctly in HubSpot.',
        },
    },
    [MARKETO]: {
        [EMPTY]: {
            title: 'No campaigns found in Marketo',
            description:
                'We couldn’t find any campaigns. This might mean there are no campaigns in Marketo or they aren’t set up to appear in Pendo.\nMake sure your campaign is active and configured correctly.',
        },
        [REJECTED]: {
            title: 'Unable to retrieve campaign list from Marketo',
            description:
                'Please double-check your integration settings and try again. If the issue continues, please contact support.',
        },
        [NO_RESULTS]: {
            title: 'No campaigns found',
            description:
                'No campaigns match your search. If you don’t see an expected campaign, make sure it’s set up correctly in Marketo.',
        },
    },
    [ELOQUA]: {
        [EMPTY]: {
            title: 'No emails found in Eloqua',
            description:
                'We couldn’t find any emails. This might mean there are no emails in Eloqua or they aren’t set up to appear in Pendo.\nMake sure your email is public and configured correctly.',
        },
        [REJECTED]: {
            title: 'Unable to retrieve email list from Eloqua',
            description:
                'Please double-check your integration settings and try again. If the issue continues, please contact support.',
        },
        [NO_RESULTS]: {
            title: 'No emails found',
            description:
                'No emails match your search. If you don’t see an expected email, ensure it’s set up correctly in Eloqua.',
        },
    },
};

export const INTEGRATION_SETUP_DETAILS = {
    [HUBSPOT]: {
        type: 'email',
        name: 'HubSpot',
        title: 'HubSpot is now available in Orchestrate',
        description: 'Set up your integration to start adding HubSpot emails to your journey.',
    },
    [MARKETO]: {
        type: 'campaign',
        name: 'Marketo',
        title: 'Marketo is now available in Orchestrate',
        description: 'Set up your integration to start adding Marketo campaigns emails to your journey.',
    },
    [ELOQUA]: {
        type: 'email',
        name: 'Eloqua',
        title: 'Eloqua is now available in Orchestrate',
        description: 'Set up your integration to start adding Eloqua emails to your journey.',
    },
};

export const INTEGRATION_SETUP_STEPS = {
    [HUBSPOT]: {
        title: 'Steps to set up your email',
        videoUrl: 'https://cdn.pendo.io/videos/hubspot/hubspot-setup.mp4',
        showAlert: false,
        fixedSize: false,
        steps: [
            {
                label: 'Open your email',
                description: 'Navigate to <strong>Marketing > Email</strong> and select the email you want to use.',
            },
            {
                label: 'Convert to single-send API',
                description:
                    'Select <strong>Actions</strong> in the top-right corner, then select <strong>Convert to single-send API.</strong>',
            },
            {
                label: 'Publish',
                description: 'Make sure all fields are complete and select <strong>Publish</strong>.',
            },
        ],
    },
    [MARKETO]: {
        title: 'Steps to set up your campaign',
        videoUrl: null,
        showAlert: true,
        alertText:
            'We recommend using a dedicated smart campaign with one Send Email step. This ensures the message is only sent once and tracked accurately in the journey.',
        fixedSize: true,
        steps: [
            { label: 'Create a Smart Campaign in Marketo' },
            {
                label: 'Add a Trigger',
                description:
                    "In the campaign's Smart List, add a Campaign is Requested trigger. Source is Web Service API.",
            },
            {
                label: 'Add an Email',
                description:
                    "In the campaign's Flow step, add the Send Email action and select which email to use.",
            },
            {
                label: 'Activate the Smart Campaign',
                description:
                    'Once activated in Marketo you would be able to see these in Pendo and add them to the journey.',
            },
        ],
    },
    [ELOQUA]: {
        title: 'Setting up emails in Oracle Eloqua',
        showAlert: false,
        fixedSize: false,
        steps: [
            {
                label: 'Create emails',
                description: 'Go to <strong>Assets > Email</strong> and create the emails you want to send.',
            },
            {
                label: 'Add emails to a campaign',
                description:
                    'Go to <strong>Orchestration > Campaigns</strong>, open your campaign, and drag the emails onto the canvas.',
            },
            {
                label: 'Activate the campaign',
                description: 'Complete any required settings, and activate the campaign. Once active, the emails will be available to select in Pendo.',
            },
            {
                label: 'Tip',
                description: 'Set the campaign end date far in the future so it stays active while Pendo manages the sends.',
                noIndex: true,
            },
        ],
    },
};
